import type { Directive, DirectiveBinding } from 'vue'

import { checkGroup } from '@/plugins/group-control'

type GroupDirectiveValue = string | string[] | undefined

interface GroupDirectiveInfo {
  parent: Node
  nextSibling: Node | null
}

const nodesMemory = new WeakMap<HTMLElement, GroupDirectiveInfo>()

const visibilityControl = (
  el: HTMLElement,
  binding: DirectiveBinding<GroupDirectiveValue>,
  negate?: boolean,
  customInfo?: GroupDirectiveInfo,
) => {
  if (!binding.value && !binding.arg) {
    console.warn('group directive requires a value or an argument.')

    return
  }

  const group = (binding.value || binding.arg) as GroupDirectiveValue
  const shouldRender = checkGroup({ groups: group!, negate })

  if (!shouldRender && el.parentNode) { // If the element should not be rendered and wasn't already removed
    el.parentNode.removeChild(el)

    return
  }

  if (!customInfo) return // If no custom info is provided, we have no way to insert the element

  if (shouldRender && !el.parentNode && customInfo.parent) customInfo.parent.insertBefore(el, customInfo.nextSibling)
}

const createGroupDirective = ({ negate = false }: { negate?: boolean } = {}): Directive => {
  const onMounted = (el: HTMLElement, binding: DirectiveBinding<GroupDirectiveValue>) => {
    nodesMemory.set(el, { parent: el.parentNode as Node, nextSibling: el.nextSibling })

    visibilityControl(el, binding, negate)
  }

  const onUpdated = (el: HTMLElement, binding: DirectiveBinding<GroupDirectiveValue>) => {
    const nodeInfo = nodesMemory.get(el)

    if (!nodeInfo) return

    visibilityControl(el, binding, negate, nodeInfo)
  }

  return {
    mounted: onMounted,
    updated: onUpdated,
  }
}

export default {
  group: createGroupDirective(),
  'not-group': createGroupDirective({ negate: true }),
}
